import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Grid,
  Chip,
  Stack,
  Avatar,
  Fade,
  Paper,
  Tooltip,
  useTheme,
  alpha,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Build as BuildIcon,
  ElectricalServices as ElectricalServicesIcon,
  Plumbing as PlumbingIcon,
  Construction as ConstructionIcon,
  Handyman as HandymanIcon,
  Engineering as EngineeringIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Work as WorkIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { settingsApi } from '../../api/settingsApi';
import { questionsApi } from '../../api/questionsApi';
import { Language, QuestionDto } from '../../types/questions';
import LoadingSpinner from '../../components/LoadingSpinner';
import { ProfessionFormModal } from '../../components/recruitment/Professions/ProfessionFormModal';
import { Profession } from '../../models/settings';

const professionIcons: { [key: string]: React.ReactElement } = {
  TRUCK_MECHANIC: <BuildIcon />,
  CNC: <EngineeringIcon />,
  ELECTRICIAN: <ElectricalServicesIcon />,
  IRON_WORKER: <ConstructionIcon />,
  WELDER: <HandymanIcon />,
  PLUMBER: <PlumbingIcon />,
  MASON: <HomeIcon />,
  PIPE_FITTER: <EngineeringIcon />,
};

// Helper function to get icon for a profession
const getProfessionIcon = (professionKey: string): React.ReactElement => {
  return professionIcons[professionKey] || <WorkIcon />;
};

const ProfessionQuestions: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedLanguage = Language.EN; // English only
  const [questionCounts, setQuestionCounts] = useState<{ [key: string]: number }>({});
  const [professionModalOpen, setProfessionModalOpen] = useState(false);
  const [editingProfession, setEditingProfession] = useState<Profession | undefined>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [professionToDelete, setProfessionToDelete] = useState<Profession | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const resolveProfessionColors = useCallback(
    (color?: string | null) => {
      const fallback = theme.palette.primary.main;

      if (!color) {
        return {
          base: fallback,
          contrast: theme.palette.getContrastText(fallback),
        };
      }

      try {
        const normalized = color.trim();
        if (!normalized) {
          return {
            base: fallback,
            contrast: theme.palette.getContrastText(fallback),
          };
        }

        const formatted = (normalized.startsWith('#') ? normalized : `#${normalized}`).toUpperCase();

        return {
          base: formatted,
          contrast: theme.palette.getContrastText(formatted),
        };
      } catch {
        return {
          base: fallback,
          contrast: theme.palette.getContrastText(fallback),
        };
      }
    },
    [theme],
  );

  useEffect(() => {
    fetchData();
  }, [selectedLanguage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [professionsData, questionsData] = await Promise.all([
        settingsApi.getProfessions(),
        questionsApi.getQuestions({ language: selectedLanguage, questionType: 'PROFESSION' })
      ]);
      
      setProfessions(professionsData);
      
      // Count questions per profession
      const counts: { [key: string]: number } = {};
      questionsData.forEach((question: QuestionDto) => {
        if (question.professionKey) {
          counts[question.professionKey] = (counts[question.professionKey] || 0) + 1;
        }
      });
      setQuestionCounts(counts);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleProfessionClick = (professionKey: string) => {
    navigate(`/questions/profession/${professionKey}?language=${selectedLanguage}`);
  };

  const handlePersonalQuestionsClick = () => {
    navigate(`/questions/personal?language=${selectedLanguage}`);
  };

  const handleAddProfessionClick = () => {
    setProfessionModalOpen(true);
  };

  const handleProfessionCreated = () => {
    fetchData();
    setSnackbar({
      open: true,
      message: 'Profession created successfully! You can now add questions to it.',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, professionKey: string) => {
    event.stopPropagation();
    setMenuAnchorEl({ ...menuAnchorEl, [professionKey]: event.currentTarget });
  };

  const handleMenuClose = (professionKey: string) => {
    setMenuAnchorEl({ ...menuAnchorEl, [professionKey]: null });
  };

  const handleEditProfession = (profession: Profession) => {
    setEditingProfession(profession);
    setProfessionModalOpen(true);
    handleMenuClose(profession.professionKey);
  };

  const handleDeleteClick = (profession: Profession) => {
    setProfessionToDelete(profession);
    setDeleteDialogOpen(true);
    handleMenuClose(profession.professionKey);
  };

  const handleDeleteConfirm = async () => {
    if (!professionToDelete) return;

    try {
      await settingsApi.deleteProfession(professionToDelete.id);
      await fetchData();
      setDeleteDialogOpen(false);
      setProfessionToDelete(null);
      setSnackbar({
        open: true,
        message: 'Profession archived successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to archive profession',
        severity: 'error'
      });
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Question Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage questions for different professions
            </Typography>
          </Box>
          <Chip
            icon={<LanguageIcon />}
            label="English Only"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Personal Questions Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Fade in timeout={300}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
              }}
              onClick={handlePersonalQuestionsClick}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                    <PersonIcon />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" fontWeight="bold">
                    Personal Questions
                  </Typography>
                }
                subheader="Common questions for all workers"
              />
              <CardContent>
                <Chip
                  label="Required for all workers"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Profession Cards */}
        {professions.map((profession, index) => {
          const { base: professionColor, contrast: professionContrast } = resolveProfessionColors(profession.colorHex);
          const cardBackground = `linear-gradient(135deg, ${alpha(professionColor, 0.12)} 0%, ${alpha(professionColor, 0.06)} 100%)`;

          return (
            <Grid item xs={12} md={6} lg={4} key={profession.id}>
              <Fade in timeout={300 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'visible',
                    background: cardBackground,
                    border: `1px solid ${alpha(professionColor, 0.2)}`,
                    borderTop: `4px solid ${alpha(professionColor, 0.5)}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                      background: `linear-gradient(135deg, ${alpha(professionColor, 0.18)} 0%, ${alpha(professionColor, 0.08)} 100%)`,
                      '& .edit-button': {
                        opacity: 1,
                      },
                    },
                  }}
                  onClick={() => handleProfessionClick(profession.professionKey)}
              >
                <CardHeader
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: professionColor,
                        color: professionContrast,
                        border: `2px solid ${alpha(professionColor, 0.25)}`,
                      }}
                    >
                      {getProfessionIcon(profession.professionKey)}
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" fontWeight="bold">
                      {profession.professionName}
                    </Typography>
                  }
                  subheader={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={professionColor}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: professionColor,
                            boxShadow: `0 0 0 2px ${alpha(professionColor, 0.25)}`,
                          }}
                        />
                      </Tooltip>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {profession.professionKey}
                      </Typography>
                    </Stack>
                  }
                  action={
                    <>
                      <Tooltip title="More options">
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, profession.professionKey)}
                          sx={{
                            opacity: 0.7,
                            transition: 'opacity 0.2s',
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={menuAnchorEl[profession.professionKey]}
                        open={Boolean(menuAnchorEl[profession.professionKey])}
                        onClose={() => handleMenuClose(profession.professionKey)}
                      >
                        <MenuItem onClick={() => handleEditProfession(profession)}>
                          <ListItemIcon>
                            <EditIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Edit Profession</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleDeleteClick(profession)}>
                          <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Archive Profession</ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  }
                />
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                    <Chip
                      label={`${questionCounts[profession.professionKey] || 0} questions`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: alpha(professionColor, 0.5),
                        color: professionColor,
                        fontWeight: 600,
                        backgroundColor: alpha(professionColor, 0.08),
                      }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={`Accent color ${professionColor}`}>
                        <Chip
                          label={professionColor}
                          size="small"
                          sx={{
                            backgroundColor: professionColor,
                            color: professionContrast,
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                      {questionCounts[profession.professionKey] === 0 && (
                        <Chip
                          label="No questions"
                          size="small"
                          color="warning"
                          variant="filled"
                        />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
              </Fade>
            </Grid>
          );
        })}

        {/* Add New Question Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Fade in timeout={300 + professions.length * 100}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: `2px dashed ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
              onClick={handleAddProfessionClick}
            >
              <CardContent
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <Avatar sx={{ bgcolor: 'transparent', color: theme.palette.primary.main, width: 56, height: 56 }}>
                  <AddIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Add New Question
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Create a new profession and start adding questions
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      <ProfessionFormModal
        open={professionModalOpen}
        onClose={() => {
          setProfessionModalOpen(false);
          setEditingProfession(undefined);
        }}
        onSuccess={handleProfessionCreated}
        profession={editingProfession}
        redirectToQuestions={!editingProfession}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Archive Profession</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive "{professionToDelete?.professionName}"? 
            This will remove the profession from the active list. All associated questions will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfessionQuestions;
